import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LeafModal } from "../../../common/leaf/LeafModal/LeafModal";
import ServerSideExportDownload from "../ServerSideExportCommon/ServerSideExportDownload/ServerSideExportDownload";
import { storedLocale } from "../../Authentication/util";
import { useAppSelector } from "../../../store";
import { selectGroupLeader } from "../../../reducers/groupLeaderFilter";
import {
  ExportFormat,
  FORMAT_XLSX,
  FORMAT_CSV,
  FORMAT_PDF,
  STATUS_IN_PROGRESS,
  STATUS_PENDING,
} from "../../../common/constants/export-constants";
import { getTranslatedFormats } from "../ServerSideExportCommon/ServerSideExportUtil/ServerSideExportFormatUtil";
import { useGetExportListV2Query, useRenewalsExportV2Mutation } from "../../../services/ExportV2/exportV2API";
import type {
  CurrentViewExportReqBody,
  ExportAffABO,
  ExportDetails,
  ExportListResponse,
  TextOverride,
} from "../../../services/ExportV2/exportV2API.types";
import {
  dispatchNotifyInProgressEvent,
  dispatchOpenExportV2ModalEvent,
} from "../ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import defaultContent from "../../../content/serverSideExport";
import {
  exportDeleteArTrack,
  exportDownloadArTrack,
  exportAnotherMenuArTrack,
} from "../../../common/tealium/Export/ServerSideExportTrack";
import {
  updateInProgressIndicator,
  isExportDisabledBySessionSsoOrigin,
  buildExportFields,
} from "../../../common/util/exportUtil";
import "./ActionReportExportModal.scss";

type ActionReportExportModalProps = {
  moduleId: string;
  selectedKpis: string[];
  affAbos: ExportAffABO[];
  startedBy: string;
  open: boolean;
  onCloseCallbackFn: () => void;
  startedByLabels?: Record<string, string>;
  fieldTitleOverrides?: Record<string, string>;
  customTriggerExport?: () => Promise<void>;
};

// Poll interval for checking export status updates (5 seconds)
const EXPORT_POLL_INTERVAL_MS = 5000;

// Re-export from shared locations for backward compatibility
export { EXPORT_FIELD_ID_BY_KPI } from "../../../common/constants/export-constants";
export { mapKpisToExportFields } from "../../../common/util/exportUtil";

export const ActionReportExportModal = ({
  moduleId,
  selectedKpis,
  affAbos,
  startedBy,
  startedByLabels,
  fieldTitleOverrides,
  open,
  onCloseCallbackFn,
  customTriggerExport,
}: ActionReportExportModalProps) => {
  const { t } = useTranslation(["serverSideExport"]);

  const locale = storedLocale();

  const boot = useAppSelector(({ boot }) => boot);
  const selectedGroupLeader = useAppSelector(selectGroupLeader);
  const { user } = boot;
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { profile } = useAppSelector((store) => store?.boot?.configuration);

  const [renewalsExport] = useRenewalsExportV2Mutation();

  const { los: isExportDisabled } = isExportDisabledBySessionSsoOrigin();

  const {
    isLoading,
    isError,
    data: exportListData,
    refetch,
  } = useGetExportListV2Query({ moduleId }, { skip: isExportDisabled });

  const typedExportListData = exportListData as ExportListResponse | undefined;

  // Use isLoading for skeleton display (only true on initial load with no cached data)
  // isFetching is true during background refetches which causes skeleton flashing
  const showSkeleton = isLoading && !typedExportListData?.Exports;

  const hasInProgressExports = useMemo(() => {
    return typedExportListData?.Exports?.some(
      (exp) => exp.ExportStatus === STATUS_IN_PROGRESS || exp.ExportStatus === STATUS_PENDING,
    );
  }, [typedExportListData?.Exports]);

  useEffect(() => {
    if (!open || isExportDisabled || !hasInProgressExports || isError) return;

    const pollInterval = setInterval(() => {
      refetch();
    }, EXPORT_POLL_INTERVAL_MS);

    return () => clearInterval(pollInterval);
  }, [open, isExportDisabled, hasInProgressExports, isError, refetch]);

  updateInProgressIndicator(typedExportListData?.Exports);

  useEffect(() => {
    if (!isExportDisabled && open) {
      refetch();
    }
  }, [user, open, refetch, isExportDisabled]);

  const getExportPayload = useCallback((): CurrentViewExportReqBody => {
    const { address, addressLinesDivider } = profile?.format || {};
    const isoCountryCode = boot?.user?.isoCountryCode || "";
    const affAbo = `${user.aff}-${user.abo}`;

    const textOverride = t("textOverride", {
      returnObjects: true,
      defaultValue: defaultContent.textOverride,
    }) as TextOverride;

    const exportFields = t("exportFields", {
      returnObjects: true,
      defaultValue: defaultContent.exportFields,
    }) as Record<string, string>;

    const fields = buildExportFields(selectedKpis, exportFields, fieldTitleOverrides);

    return {
      moduleId,
      reportAffAbo: affAbo,
      period: selectedPeriod,
      locale,
      textOverride,
      fields,
      countryCode: isoCountryCode,
      format: {
        address: {
          pattern: address || "",
          addressLineDivider: addressLinesDivider || "",
        },
      },
      affAbos,
      startedBy,
      filteredByKPIS: selectedKpis.length > 0,
      ...(selectedGroupLeader ? { groupLeader: { aff: selectedGroupLeader.aff, abo: selectedGroupLeader.abo } } : {}),
    };
  }, [
    moduleId,
    selectedKpis,
    affAbos,
    profile,
    boot,
    user,
    selectedPeriod,
    locale,
    t,
    fieldTitleOverrides,
    selectedGroupLeader,
  ]);

  const onExportAnotherClick = useCallback(async () => {
    exportAnotherMenuArTrack(moduleId, typedExportListData?.Exports?.length ? true : false);

    if (isExportDisabled) {
      return;
    }

    dispatchOpenExportV2ModalEvent(moduleId, false);
    onCloseCallbackFn();

    try {
      if (customTriggerExport) {
        await customTriggerExport();
      } else {
        const exportPayload = getExportPayload();
        await renewalsExport(exportPayload);
      }
      dispatchNotifyInProgressEvent();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to trigger export for module "${moduleId}":`, error);
    }
  }, [
    moduleId,
    isExportDisabled,
    getExportPayload,
    renewalsExport,
    onCloseCallbackFn,
    typedExportListData?.Exports?.length,
    customTriggerExport,
  ]);

  const downloadButtonOnClick = (_exportDetails: ExportDetails, fileFormat: string, moduleId: string) => {
    exportDownloadArTrack(fileFormat, moduleId);
  };

  const deleteButtonOnClick = (exportDetails: ExportDetails, moduleId: string) => {
    const { ExportStatus } = exportDetails;
    exportDeleteArTrack(ExportStatus, moduleId);
  };

  const supportedFormats: ExportFormat[] = [FORMAT_XLSX, FORMAT_CSV, FORMAT_PDF];

  return (
    <div className="action-report-export-modal">
      <LeafModal open={open} onCloseCallbackFn={onCloseCallbackFn} id="action-report-export-modal">
        <ServerSideExportDownload
          title={`${t("availableExportModal.header", defaultContent.availableExportModal.header)}`}
          subTitle={t("availableExportModal.subheader", {
            formats: getTranslatedFormats(t, supportedFormats, defaultContent),
            defaultValue: defaultContent.availableExportModal.subheader,
          })}
          exportTypeContent={{
            currentView: `${t(
              "exportTypeModal.menu.los.currentView",
              defaultContent.exportTypeModal.menu.los.currentView,
            )}`,
          }}
          startedByLabels={startedByLabels}
          exports={typedExportListData?.Exports}
          downloadButtonOnClick={downloadButtonOnClick}
          deleteButtonOnClick={deleteButtonOnClick}
          show={true}
          showExportAnotherButton={true}
          exportAnotherButton={{
            exportText: `${t("exportTypeModal.exportButton", defaultContent.exportTypeModal.exportButton)}`,
            exportAnotherText: `${t(
              "availableExportModal.exportAnotherButton",
              defaultContent.availableExportModal.exportAnotherButton,
            )}`,
            active: affAbos.length > 0,
            onClick: onExportAnotherClick,
          }}
          moduleId={moduleId}
          isFetching={showSkeleton}
        />
      </LeafModal>
    </div>
  );
};

export default ActionReportExportModal;
